import { EventType } from '../experiments/Events.ts';
import type { EventLogger } from '../experiments/EventLogger.ts';
import type { GameState } from '../game/GameState.ts';
import type { ButtonEvent, InputDevice } from '../hardware/InputDevice.ts';
import { EdgeLatch, noFlap, type Controller, type ControllerOutput } from './Controller.ts';

/**
 * A physical switch controls the bird.
 *
 * This is the controller a participant uses directly, and -- importantly -- it
 * is the same code path a *stimulated* participant's movement travels through.
 * `StimulationController` reuses the detection logic here rather than
 * reimplementing it, so a flap caused by a stimulated wrist and a flap caused by
 * a voluntary press are measured identically.
 *
 * Every button transition is logged as BUTTON_DOWN / BUTTON_UP with its own
 * timestamp, separate from the FLAP_EXECUTED that follows. The gap between
 * those two is the simulation's input quantisation, and it is the last term in
 * the latency budget.
 */
export class ExternalButtonController implements Controller {
  readonly id = 'external-button';
  readonly label: string;

  protected latch = new EdgeLatch();
  private unsubscribe: (() => void) | null = null;
  private downCount = 0;
  private upCount = 0;
  private lastDownAt: number | null = null;

  constructor(
    protected readonly device: InputDevice,
    protected readonly logger: EventLogger,
    /** Read by the logger so events land on the right tick. */
    protected readonly getTick: () => number,
  ) {
    this.label = `External button (${device.label})`;
  }

  async attach(): Promise<void> {
    if (this.unsubscribe) return;
    if (this.device.getStatus() !== 'connected') {
      await this.device.connect();
    }
    this.unsubscribe = this.device.onButton((e) => this.onButton(e));
  }

  async detach(): Promise<void> {
    this.unsubscribe?.();
    this.unsubscribe = null;
    this.latch.clear();
  }

  reset(): void {
    this.latch.clear();
  }

  update(_state: GameState): ControllerOutput {
    const edge = this.latch.consume();
    if (!edge) return noFlap();
    return { flap: true, timestamp: edge.timestamp, metadata: edge.meta };
  }

  status(): Record<string, unknown> {
    return {
      device: this.device.id,
      connection: this.device.getStatus(),
      down: this.downCount,
      up: this.upCount,
      lastDownAt: this.lastDownAt,
      ...(this.device.info?.() ?? {}),
    };
  }

  protected onButton(e: ButtonEvent): void {
    if (e.down) {
      this.downCount++;
      this.lastDownAt = e.timestamp;
      this.logger.log(EventType.BUTTON_DOWN, {
        tick: this.getTick(),
        timestamp: e.timestamp,
        requestId: e.requestId,
        metadata: { source: e.source, ...e.metadata },
      });
      this.latch.set(e.timestamp, { source: e.source, requestId: e.requestId });
    } else {
      this.upCount++;
      this.logger.log(EventType.BUTTON_UP, {
        tick: this.getTick(),
        timestamp: e.timestamp,
        requestId: e.requestId,
        metadata: { source: e.source },
      });
    }
  }
}
